import * as d3 from 'd3';

/**
 * D3 Family Tree Chart (Plain/Hierarchical Layout)
 * Creates an interactive tree visualization for family relationships
 * 
 * @param {Object} data - Hierarchical data structure with family members
 * @param {Object} options - Configuration options
 * @param {Function} options.label - Function to get label from node data
 * @param {Function} options.title - Function to get title/hover text from node
 * @param {Function} options.onNodeClick - Callback when node is clicked
 * @param {number} options.width - Chart width
 * @param {number} options.height - Chart height
 * @param {number} options.margin - Chart margin
 * @param {string} options.selectedPersonId - ID of the selected person (highlighted)
 * @param {string} options.textColor - Text color for labels
 * @param {string} options.selectedTextColor - Text color for selected person
 */
export function FamilyTree(data, options = {}) {
  // Default options
  const {
    label = (d) => d.name || d.first_name || 'Unknown',
    title = (d) => `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Unknown',
    onNodeClick = null,
    width = 1000,
    height = 800,
    margin = 100,
    selectedPersonId = null,
    textColor = "#333",
    selectedTextColor = "#F59E0B"
  } = options;

  // Detect mobile screen size - use window width as primary indicator, fallback to width parameter
  // This is more reliable than checking container dimensions which might not be ready yet
  const isMobile = typeof window !== 'undefined' && window.innerWidth 
    ? window.innerWidth < 768 
    : (width && width > 0 && width < 768);
  
  // Create hierarchy from data first to analyze structure
  const root = d3.hierarchy(data);
  root.descendants().forEach((d, i) => {
    d.id = i;
    d._children = d.children;
  });

  // Calculate tree statistics for optimal spacing (Tidy Tree concept)
  // This approach is based on Tree.js (d3/charts/tree.js) but adapts spacing based on node count
  const maxDepth = root.height; // Maximum depth/levels in the tree
  
  // Count nodes at each level to determine maximum width at any level
  // This helps calculate optimal horizontal spacing
  const nodesByLevel = {};
  root.each((d) => {
    const depth = d.depth;
    nodesByLevel[depth] = (nodesByLevel[depth] || 0) + 1;
  });
  
  // Find the maximum number of nodes at any single level (widest level)
  // This is crucial for horizontal spacing - more nodes = need more space horizontally
  const maxNodesAtLevel = Math.max(...Object.values(nodesByLevel), 1);
  
  // Calculate optimal spacing based on tree structure
  // In D3 tree layout with horizontal orientation (root left, children right, using linkHorizontal):
  // - With nodeSize([dx, dy]) and translate(${d.y},${d.x}):
  //   * dx affects d.x (vertical coordinate) = vertical spacing between nodes at same level
  //   * dy affects d.y (horizontal coordinate) = horizontal spacing between levels (left-to-right)
  // Since labels include both member and spouse: "Name (Spouse Name)", we need MORE horizontal space between levels (dy)
  
  // Base vertical spacing between nodes at same level (dx)
  const baseDx = isMobile ? 20 : 15; // Vertical spacing between nodes at same level
  const baseDy = (width && width > 0) ? width / (isMobile ? 3.5 : 5) : (isMobile ? 220 : 180); // Base horizontal spacing between levels (increased for labels)
  
  // Adaptive vertical spacing between nodes at same level (dx): 
  // - This is the spacing between siblings/peers at the same tree level (top-to-bottom)
  // - Moderate increase for mobile readability
  const minDx = isMobile ? 15 : 12;
  const maxDx = isMobile ? 25 : 20;
  const depthFactor = maxDepth > 0 ? Math.min(0.25, maxDepth * 0.03) : 0;
  const dx = Math.max(minDx, Math.min(maxDx, baseDx * (1 - depthFactor)));
  
  // Adaptive horizontal spacing between levels (dy) - THIS IS THE KEY ONE FOR LABELS:
  // - Labels are "Name (Spouse Name)" format which can be 150-200px wide
  // - This controls spacing between different tree levels (left-to-right progression)
  // - Need generous spacing to prevent label overlap between levels when labels include spouse names
  const nodeDensity = Math.max(1, maxNodesAtLevel);
  const dyScale = Math.max(0.8, Math.min(1.4, Math.sqrt(10 / nodeDensity)));
  // Estimated label width for "PreferredName LastName (SpousePreferredName SpouseLastName)"
  // Can be quite long: "John Smith (Jane Johnson)" = ~180-220px depending on font size
  const estimatedLabelWidth = isMobile ? 200 : 180; // Increased estimate for labels with spouse names
  const labelPadding = isMobile ? 1.5 : 1.4; // 50% padding on mobile, 40% on desktop for labels with spouse
  const minDy = estimatedLabelWidth * labelPadding; // Minimum horizontal spacing between levels to prevent label overlap
  const calculatedDy = baseDy * dyScale;
  // Use the larger of calculated or minimum to ensure labels don't overlap
  const dy = Math.max(minDy, calculatedDy);

  // Create tree layout with calculated optimal spacing
  const tree = d3.tree().nodeSize([dx, dy]);

  root.x0 = dy / 2;
  root.y0 = 0;

  // Compute the tree layout using Tidy Tree algorithm
  tree(root);

  // Calculate bounds of the tree layout
  let x0 = Infinity;
  let x1 = -x0;
  let y0 = Infinity;
  let y1 = -y0;
  root.each((d) => {
    if (d.x > x1) x1 = d.x;
    if (d.x < x0) x0 = d.x;
    if (d.y > y1) y1 = d.y;
    if (d.y < y0) y0 = d.y;
  });

  // Calculate actual tree content dimensions (tree layout bounds)
  const treeContentWidth = x1 - x0 || 1; // Avoid division by zero
  const treeContentHeight = y1 - y0 || 1;

  // Calculate tree dimensions with margins for rendering
  const treeWidth = treeContentWidth + 2 * margin;
  const treeHeight = treeContentHeight + 2 * margin;

  // Determine SVG size: use container dimensions if provided, otherwise use provided or calculated dimensions
  let svgWidth, svgHeight;
  const containerElement = options.container 
    ? (typeof options.container === 'string' ? d3.select(options.container).node() : options.container)
    : null;
  
  if (containerElement) {
    // Get container dimensions
    const containerRect = containerElement.getBoundingClientRect();
    svgWidth = containerRect.width || containerElement.clientWidth || width || Math.max(treeWidth, 1000);
    svgHeight = containerRect.height || containerElement.clientHeight || height || Math.max(treeHeight, 800);
    
    // Ensure minimum size based on tree
    svgWidth = Math.max(svgWidth, treeWidth);
    svgHeight = Math.max(svgHeight, treeHeight);
  } else {
    // Use provided dimensions or calculate from tree
    svgWidth = width && width > 0 ? width : Math.max(treeWidth, 1000);
    svgHeight = height && height > 0 ? height : Math.max(treeHeight, 800);
  }

  // Create SVG with calculated size
  const svg = d3.create("svg")
    .attr("viewBox", [0, 0, svgWidth, svgHeight])
    .attr("width", svgWidth)
    .attr("height", svgHeight)
    .attr("style", "font: 12px sans-serif; width: 100%; height: 100%; display: block;");

  // Store tree dimensions and layout info on SVG node for fitToWindow to access
  svg.node()._treeData = {
    treeWidth,
    treeHeight,
    treeContentWidth,
    treeContentHeight,
    margin,
    x0,
    y0,
    svgWidth,
    svgHeight
  };

  // Create a container group for zoom/pan
  const container = svg.append("g");

  // Add zoom behavior
  const zoom = d3.zoom()
    .scaleExtent([0.1, 4]) // Allow zoom from 10% to 400%
    .on("zoom", (event) => {
      container.attr("transform", event.transform);
    });

  svg.call(zoom);

  // Function to auto-fit the tree to the viewport
  const fitToWindow = () => {
    const svgElement = svg.node();
    if (!svgElement || !svgElement._treeData) return;

    // Get stored tree data
    const { treeWidth: storedTreeWidth, treeHeight: storedTreeHeight, margin: storedMargin, x0: storedX0, y0: storedY0, svgWidth: storedSvgWidth, svgHeight: storedSvgHeight } = svgElement._treeData;

    // Get the actual displayed size of the SVG (may be constrained by CSS)
    const rect = svgElement.getBoundingClientRect();
    const currentWidth = rect.width || storedSvgWidth;
    const currentHeight = rect.height || storedSvgHeight;

    if (currentWidth <= 0 || currentHeight <= 0) return;

    // Calculate scale to fit the tree content with padding
    const padding = 40;
    const availableWidth = currentWidth - padding;
    const availableHeight = currentHeight - padding;

    if (availableWidth <= 0 || availableHeight <= 0) return;

    // Calculate scale based on tree dimensions
    const scale = Math.min(
      availableWidth / storedTreeWidth,
      availableHeight / storedTreeHeight,
      1 // Don't zoom in beyond 100%
    );

    // Calculate center position
    // The tree group (g) is positioned at (margin - x0, margin - y0) relative to the container
    // We need to translate so the scaled tree is centered in the viewport
    const scaledTreeWidth = storedTreeWidth * scale;
    const scaledTreeHeight = storedTreeHeight * scale;
    
    // Center the tree in the viewport
    const translateX = (currentWidth - scaledTreeWidth) / 2 - (storedMargin - storedX0) * scale;
    const translateY = (currentHeight - scaledTreeHeight) / 2 - (storedMargin - storedY0) * scale;

    // Apply transform
    svg.call(
      zoom.transform,
      d3.zoomIdentity.translate(translateX, translateY).scale(scale)
    );
  };

  // Apply initial transform after a brief delay to ensure DOM is ready
  // This allows the container to be measured correctly
  setTimeout(() => {
    fitToWindow();
  }, 50);

  // Store fit function on the SVG element for external access
  svg.node().fitToWindow = fitToWindow;

  // Auto-fit on window resize (debounced)
  let resizeTimeout;
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      fitToWindow();
    }, 250);
  };
  
  window.addEventListener('resize', handleResize);
  
  // Clean up resize listener when SVG is removed
  const originalRemove = svg.node().remove;
  svg.node().remove = function() {
    window.removeEventListener('resize', handleResize);
    if (originalRemove) originalRemove.call(this);
  };

  const g = container.append("g")
    .attr("transform", `translate(${margin - x0},${margin - y0})`);

  // Add links
  const linkGroup = g.append("g")
    .attr("fill", "none")
    .attr("stroke", "#555")
    .attr("stroke-opacity", 0.4)
    .attr("stroke-width", 1.5)
    .selectAll("path")
    .data(root.links())
    .join("path")
    .attr("d", d3.linkHorizontal()
      .x(d => d.y)
      .y(d => d.x));

  // Add nodes
  const node = g.append("g")
    .attr("stroke-linejoin", "round")
    .attr("stroke-width", 3)
    .selectAll("g")
    .data(root.descendants())
    .join("g")
    .attr("transform", d => `translate(${d.y},${d.x})`);

  // Check if node is selected person
  const isSelected = (d) => {
    return selectedPersonId && (d.data.id === selectedPersonId || d.data.person_id === selectedPersonId);
  };

  // Get node color based on gender and selection
  const getNodeColor = (d) => {
    if (isSelected(d)) return '#F59E0B'; // Gold for selected
    if (d.data.sex === 'male') return '#3B82F6'; // Blue for male
    if (d.data.sex === 'female') return '#EC4899'; // Pink for female
    return '#999'; // Gray for unknown
  };

  // Add circles for nodes
  node.append("circle")
    .attr("fill", d => getNodeColor(d))
    .attr("r", d => isSelected(d) ? 5 : 3.5)
    .attr("stroke", d => isSelected(d) ? "#FCD34D" : "none")
    .attr("stroke-width", d => isSelected(d) ? 2 : 0);

  // Add labels - simple text format "Member (Spouse Name)"
  // Responsive font size: larger on mobile to prevent overlap and improve readability
  const fontSize = isMobile ? "12px" : "10px";
  const textOffset = isMobile ? 8 : 6;
  const nodeText = node.append("text")
    .attr("dy", "0.32em")
    .attr("x", d => d.children ? -textOffset : textOffset)
    .attr("text-anchor", d => d.children ? "end" : "start")
    .text(d => label(d.data))
    .style("fill", d => isSelected(d) ? selectedTextColor : textColor)
    .style("font-weight", d => isSelected(d) ? "bold" : "normal")
    .style("font-size", fontSize);

  // Add click handlers for text labels if callback provided
  if (onNodeClick) {
    nodeText
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        onNodeClick(d.data);
      });
  }

  // Add titles (tooltips)
  node.append("title")
    .text((d) => title(d.data));

  // Add click handlers for the circle if callback provided
  if (onNodeClick) {
    node.select("circle")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        onNodeClick(d.data);
      });
  }

  // Return the SVG element
  return svg.node();
}

/**
 * Render Family Tree chart to a container
 * 
 * @param {Object} data - Hierarchical data structure
 * @param {Object} options - Configuration options
 * @param {string|HTMLElement} options.container - Container selector or element
 */
export function renderFamilyTree(data, options = {}) {
  const { container, ...treeOptions } = options;
  
  if (!container) {
    throw new Error("Container is required");
  }

  const containerElement = typeof container === 'string' 
    ? d3.select(container).node() 
    : container;

  if (!containerElement) {
    throw new Error(`Container not found: ${container}`);
  }

  // Clear container
  d3.select(containerElement).selectAll("*").remove();

  // Create and append tree
  const svg = FamilyTree(data, treeOptions);
  containerElement.appendChild(svg);

  return svg;
}

export default FamilyTree;
